import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ProductoService } from '../../core/services/producto.service';
import { CategoriaService } from '../../core/services/categoria.service';
import { CatalogoService } from '../../core/services/catalogo.service';
import { ImagenService } from '../../core/services/imagen.service';
import { AlertService } from '../../core/services/alert.service';
import { CategoriaMapper } from '../../core/mappers/categoria.mapper';
import { CategoriaApi } from '../../core/models/api/categoria-api';
import { CatalogoItemApi } from '../../core/models/api/auth-api';
import { Constante } from '../../constante';
import { PhotoGalleryEditorComponent, PhotoSlot } from '../../shared/components/photo-gallery-editor.component';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';

@Component({
    selector: 'app-admin-producto-form',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, PhotoGalleryEditorComponent, UiLoadingComponent],
    template: `
    <a routerLink="/admin/productos" class="text-sm font-semibold text-rose-500 hover:underline">← Productos</a>
    <div class="flex items-end justify-between gap-4 mt-2 mb-6">
      <div>
        <h2 class="text-2xl font-black text-gray-800">{{ uuid() ? 'Editar producto' : 'Nuevo producto' }}</h2>
        <p class="text-sm text-gray-400 mt-1">Las fotos se ven aquí primero y se suben al guardar.</p>
      </div>
    </div>

    <app-ui-loading *ngIf="booting()"></app-ui-loading>

    <form *ngIf="!booting()" class="grid grid-cols-1 lg:grid-cols-12 gap-6" (ngSubmit)="guardar()">
      <section class="lg:col-span-5 bg-white rounded-[2rem] p-5 border border-rose-100 shadow-sm h-fit lg:sticky lg:top-24">
        <app-photo-gallery-editor
          [photos]="fotos()"
          (photosChange)="fotos.set($event)"
          title="Galería"
          hint="JPEG, PNG o WebP. La primera es la foto principal.">
        </app-photo-gallery-editor>
      </section>

      <section class="lg:col-span-7 flex flex-col gap-5">
        <div class="bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-4">
          <label class="md:col-span-2 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre</span>
            <input [ngModel]="nombre()" (ngModelChange)="nombre.set($event)" name="nombre" required placeholder="Ramo de rosas" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
          </label>
          <label class="block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">SKU</span>
            <input [ngModel]="sku()" (ngModelChange)="sku.set($event)" name="sku" required placeholder="RA01" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
          </label>
          <label class="block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Tipo</span>
            <select [ngModel]="tipoProductoId()" (ngModelChange)="tipoProductoId.set($event)" name="tipoProductoId" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200 bg-white">
              <option *ngFor="let t of tipos()" [ngValue]="t.id">{{ t.nombre }}</option>
            </select>
          </label>
          <label class="block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Precio (S/)</span>
            <input [ngModel]="precio()" (ngModelChange)="precio.set($event)" name="precio" type="number" step="0.01" min="0" required class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
          </label>
          <label class="block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Precio oferta</span>
            <input [ngModel]="precioOferta()" (ngModelChange)="precioOferta.set($event)" name="precioOferta" type="number" step="0.01" min="0" placeholder="Opcional" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
          </label>
          <label class="block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Stock</span>
            <input [ngModel]="stockTotal()" (ngModelChange)="stockTotal.set($event)" name="stockTotal" type="number" min="0" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
          </label>
          <label class="md:col-span-2 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Descripción</span>
            <textarea [ngModel]="descripcion()" (ngModelChange)="descripcion.set($event)" name="descripcion" rows="4" placeholder="Qué incluye, ocasión, tamaño..." class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200"></textarea>
          </label>
        </div>

        <div class="bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm">
          <p class="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Categorías</p>
          <div class="flex flex-wrap gap-2">
            <label *ngFor="let c of categorias()" class="cursor-pointer">
              <input type="checkbox" class="peer sr-only" [checked]="categoriaUuids().includes(c.uuid)" (change)="toggleCat(c.uuid)" />
              <span class="block text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 text-gray-600 peer-checked:bg-rose-500 peer-checked:text-white peer-checked:border-rose-500">{{ c.nombre }}</span>
            </label>
          </div>
        </div>

        <div class="bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm flex flex-wrap gap-5">
          <label class="flex items-center gap-2 text-sm text-gray-700 font-medium">
            <input type="checkbox" [ngModel]="esNuevo()" (ngModelChange)="esNuevo.set($event)" name="esNuevo" class="accent-rose-500" /> Nuevo
          </label>
          <label class="flex items-center gap-2 text-sm text-gray-700 font-medium">
            <input type="checkbox" [ngModel]="esDestacado()" (ngModelChange)="esDestacado.set($event)" name="esDestacado" class="accent-rose-500" /> Destacado
          </label>
          <label class="flex items-center gap-2 text-sm text-gray-700 font-medium">
            <input type="checkbox" [ngModel]="visibleCatalogo()" (ngModelChange)="visibleCatalogo.set($event)" name="visibleCatalogo" class="accent-rose-500" /> Visible en catálogo
          </label>
        </div>

        <div class="flex gap-3">
          <button type="submit" [disabled]="loading()" class="flex-1 py-3.5 rounded-2xl text-white font-bold text-sm disabled:opacity-60" style="background-color: var(--bg-main);">
            {{ loading() ? 'Guardando...' : 'Guardar producto' }}
          </button>
          <a routerLink="/admin/productos" class="px-5 py-3.5 rounded-2xl text-sm font-bold text-gray-500 border border-gray-200 bg-white">Cancelar</a>
        </div>
      </section>
    </form>
    `
})
export class AdminProductoFormComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private productoService = inject(ProductoService);
    private categoriaService = inject(CategoriaService);
    private catalogoService = inject(CatalogoService);
    private imagenService = inject(ImagenService);
    private alerts = inject(AlertService);

    uuid = signal<string | null>(null);
    nombre = signal('');
    sku = signal('');
    precio = signal(0);
    precioOferta = signal<number | null>(null);
    stockTotal = signal(10);
    tipoProductoId = signal(0);
    descripcion = signal('');
    esNuevo = signal(false);
    esDestacado = signal(false);
    visibleCatalogo = signal(true);
    categoriaUuids = signal<string[]>([]);
    fotos = signal<PhotoSlot[]>([]);
    complementoUuids = signal<string[]>([]);
    relacionadoUuids = signal<string[]>([]);
    slugActual = signal('');

    categorias = signal<CategoriaApi[]>([]);
    tipos = signal<CatalogoItemApi[]>([]);
    booting = signal(true);
    loading = signal(false);

    ngOnInit(): void {
        const routeUuid = this.route.snapshot.paramMap.get('uuid');
        this.uuid.set(routeUuid && routeUuid !== 'nuevo' ? routeUuid : null);

        this.catalogoService.listTiposProducto().subscribe({
            next: (tipos) => {
                this.tipos.set(tipos);
                if (!this.tipoProductoId()) {
                    this.tipoProductoId.set(tipos.find((t) => t.codigo === 'PRODUCTO')?.id ?? tipos[0]?.id ?? 0);
                }
            }
        });
        this.categoriaService.listCategorias().subscribe({
            next: (cats) => this.categorias.set(CategoriaMapper.flatten(cats))
        });

        const id = this.uuid();
        if (!id) {
            this.booting.set(false);
            return;
        }

        this.productoService.getProductoBySlug(id).subscribe({
            next: (p) => {
                if (p) {
                    this.uuid.set(p.uuid);
                    this.nombre.set(p.nombre);
                    this.sku.set(p.sku);
                    this.precio.set(p.precio);
                    this.precioOferta.set(p.precioOferta);
                    this.stockTotal.set(p.stockTotal ?? p.stockDisponible);
                    this.tipoProductoId.set(p.tipoProductoId);
                    this.descripcion.set(p.descripcion ?? '');
                    this.esNuevo.set(!!p.esNuevo);
                    this.esDestacado.set(!!p.esDestacado);
                    this.visibleCatalogo.set(!!p.visibleCatalogo);
                    this.slugActual.set(p.slug);
                    this.categoriaUuids.set(p.categoriaUuids ?? []);
                    this.fotos.set((p.imagenes ?? []).map((i) => ({
                        previewUrl: i.urlMedium || i.urlOriginal,
                        imagenUuid: i.imagenUuid,
                        file: null
                    })));
                    this.complementoUuids.set((p.complementos ?? []).map((c) => c.uuid));
                    this.relacionadoUuids.set((p.relacionados ?? []).map((r) => r.uuid));
                }
                this.booting.set(false);
            },
            error: () => {
                this.booting.set(false);
                this.alerts.error('No se pudo cargar el producto.');
            }
        });
    }

    toggleCat(uuid: string) {
        const current = this.categoriaUuids();
        this.categoriaUuids.set(
            current.includes(uuid) ? current.filter((id) => id !== uuid) : [...current, uuid]
        );
    }

    guardar() {
        this.loading.set(true);
        const pending = this.fotos().filter((f) => !!f.file).map((f) => f.file as File);
        if (pending.length === 0) {
            this.enviar(this.fotos().map((f) => f.imagenUuid).filter((id): id is string => !!id));
            return;
        }

        this.imagenService.upload(pending).subscribe({
            next: (imgs) => {
                let i = 0;
                const uuids = this.fotos().map((f) => f.imagenUuid || imgs[i++]?.uuid).filter((id): id is string => !!id);
                this.enviar(uuids);
            },
            error: (err) => {
                this.loading.set(false);
                this.alerts.error(err?.message || 'No se pudieron subir las fotos.');
            }
        });
    }

    private enviar(imagenUuids: string[]) {
        const body = {
            tipoProductoId: this.tipoProductoId(),
            sku: this.sku(),
            nombre: this.nombre(),
            slug: this.slugActual() || undefined,
            descripcion: this.descripcion(),
            precio: Number(this.precio()),
            precioOferta: this.precioOferta() ? Number(this.precioOferta()) : null,
            impuestosIncluidos: true,
            esNuevo: this.esNuevo(),
            esDestacado: this.esDestacado(),
            visibleCatalogo: this.visibleCatalogo(),
            stockTotal: Number(this.stockTotal()) || 0,
            categoriaUuids: this.categoriaUuids(),
            imagenUuids,
            complementoUuids: this.complementoUuids(),
            relacionadoUuids: this.relacionadoUuids()
        };

        const done = (success: boolean) => {
            this.loading.set(false);
            if (success) {
                this.alerts.ok('Producto guardado');
                this.router.navigate([Constante.URL_ADMIN + '/productos']);
            } else {
                this.alerts.error('No se pudo guardar el producto');
            }
        };

        const id = this.uuid();
        if (id) {
            this.productoService.updateProducto(id, body).subscribe({
                next: (ok) => done(!!ok),
                error: () => {
                    this.loading.set(false);
                    this.alerts.error('No se pudo guardar el producto');
                }
            });
        } else {
            this.productoService.createProducto(body).subscribe({
                next: (created) => done(!!created),
                error: () => {
                    this.loading.set(false);
                    this.alerts.error('No se pudo guardar el producto');
                }
            });
        }
    }
}
